import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Divider,
  Button,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { initiateCspmScan, clearAllCspmMessages, getCspmChecks } from '../../../store/cspm/cspmSlice';

const InitiateScanModal = ({ open, onClose, accountsWithWorkloads, selectedAccount, selectedWorkload, onSuccess }) => {
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const { initiateCspmScanMessage, initiateCspmScanError, isInitiatingCspmScan } = useSelector(state => state.cspm);

  // Find workload name for display and payload
  let workloadName, scheduleId, scheduleType;
  if (selectedWorkload) {
    let workloadObj = null;
    for (const acc of accountsWithWorkloads) {
      workloadObj = (acc.workloads || []).find(w => w.id === selectedWorkload) ||
                    (acc.schedules || []).find(s => s.scheduleId === selectedWorkload);
      if (workloadObj) break;
    }
    workloadName = workloadObj?.name || workloadObj?.scheduleName;
    scheduleId = workloadObj?.scheduleId;
    scheduleType = workloadObj?.scheduleType;
  }

  // Handle API responses
  useEffect(() => {
    if (initiateCspmScanMessage) {
      if (onSuccess) onSuccess(initiateCspmScanMessage);
      dispatch(clearAllCspmMessages());
      onClose();
    } else if (initiateCspmScanError) {
      setSnackbar({ open: true, message: initiateCspmScanError, severity: 'error' });
      dispatch(clearAllCspmMessages());
    }
    // eslint-disable-next-line
  }, [initiateCspmScanMessage, initiateCspmScanError]);

  useEffect(() => {
    if (!open) {
      setSnackbar({ open: false, message: '', severity: 'error' });
      dispatch(clearAllCspmMessages());
    }
    // eslint-disable-next-line
  }, [open]);

  const handleProceed = () => {
    const payload = {
      accountNumber: selectedAccount,
      ...(scheduleId && { scheduleId }),
      ...(scheduleType && { scheduleType }),
      ...(workloadName && { workloadName })
    };
    dispatch(initiateCspmScan(payload));
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="initiate-scan-modal"
        aria-describedby="modal-for-initiating-scan"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: '10px',
            boxShadow: 24,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F5F5F5',
              padding: '16px 24px',
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              borderBottom: '1px solid #E0E0E0',
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Initiate Scan
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          {/* Content */}
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Are you sure you want to initiate a scan for<br />
              <b>Account:</b> {selectedAccount}
              {workloadName && (
                <>
                  <br />
                  <b>Workload:</b> {workloadName}
                </>
              )}
              ?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onClose}
                disabled={isInitiatingCspmScan}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleProceed}
                disabled={isInitiatingCspmScan}
                startIcon={isInitiatingCspmScan ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {isInitiatingCspmScan ? 'Processing...' : 'Proceed'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InitiateScanModal; 